import { Remote, wrap } from "comlink";
import type { RealTimeDataPacket, IPacketWorkerApi, PlotKey, MultiMotorInfo } from "./packet.worker";
import { splitMultiMotorPlotKey } from "./packet.worker";
export { multiMotorPrefixFromNodeId } from './packet.worker';
import {
    IMultiMotorTelemetryMessage,
    IRealtimeDataMessage,
    PacketId,
    displayUnits,
} from "./common-types";
import { TimeWindow } from "../hooks/usePlayback";
import { ExportType } from "./hid-export";
import { TelemIdxType, TelemValue, TelemToml } from "cstore/telem";
import { telem } from "cstore/toml/toml";
import { SlntHeader, VariableTelemSamples, MultiMotorTelemSamples, SubActuators } from "./slnt-file";

// Mirrored main-thread state for plotKeyToMultiMotorInfo
let _subActuators: SubActuators = {};

const _telemSetListeners: ( () => void )[] = [];
const _notifyTelemSet = () => _telemSetListeners.forEach( l => l() );

const _subActuatorsListeners: ( () => void )[] = [];
const _notifySubActuators = () => _subActuatorsListeners.forEach( l => l() );

// The following group of exports are only for hooks
export const subscribeTelemSet = ( fn: () => void ) => {
    _telemSetListeners.push( fn );
    return () => { const i = _telemSetListeners.indexOf( fn ); if ( i !== -1 ) _telemSetListeners.splice( i, 1 ); };
};
export const subscribeSubActuators = ( fn: () => void ) => {
    _subActuatorsListeners.push( fn );
    return () => { const i = _subActuatorsListeners.indexOf( fn ); if ( i !== -1 ) _subActuatorsListeners.splice( i, 1 ); };
};
export const getTelemSet = (): { fieldName: string, fieldType: TelemIdxType }[] => _telemSet;
export const getSubActuators = (): SubActuators => _subActuators;

export const multiMotorNodeId = ( plotKey: PlotKey ): number | undefined => {
    const match = plotKey.match( /^MultiMotor_N(\d+)_/ );
    return match ? Number( match[1] ) : undefined;
};

export const plotKeyToMultiMotorInfo = ( plotKey: PlotKey ): MultiMotorInfo | undefined => {
    const parts = splitMultiMotorPlotKey( plotKey );
    if ( !parts ) return undefined;
    const { nodeId, baseName } = parts;
    const info = _subActuators[ nodeId ];
    if ( info === undefined ) return undefined;
    return { plotKey, label: info.disposition, baseName };
};

// Previously this was just navigator.hardwareConcurrency but it caused high
// memory usage. Memory is more precious than CPU.
const NUM_THREADS = Math.min( navigator.hardwareConcurrency, 4 );

type Thread = {
    api: Remote<IPacketWorkerApi>;
    numTasks: number;
};

export type ServoPulseTransformFn = ( ( devicePosition: number) => number ) | undefined;

type RealtimeMetadata = {
    length: number;
    timeWindow: TimeWindow | undefined;
};

const defaultRealtimeMetadata = (): RealtimeMetadata => ({
    length: 0,
    timeWindow: undefined,
});

export let realtimeMetadata = defaultRealtimeMetadata();

const updateTimeWindow = ( timestampMs: number ) => {
    realtimeMetadata.length++;
    if ( realtimeMetadata.timeWindow === undefined ) {
        realtimeMetadata.timeWindow = [ timestampMs, timestampMs ];
    } else {
        realtimeMetadata.timeWindow[ 1 ] = timestampMs;
    }
}

// Moved from packet.worker due to rollup issue
export const displayUnit = ( plotKey: PlotKey ): string => {
    const realTimeDataDisplayUnits = displayUnits[ PacketId.REALTIME_DATA ];
    if ( plotKey in realTimeDataDisplayUnits ) {
        return realTimeDataDisplayUnits[ plotKey as keyof IRealtimeDataMessage ];
    }
    const multiMotorTelemDisplayUnits = displayUnits[ PacketId.MULTI_MOTOR_TELEMETRY ];
    // Try to get the field name within multi-motor telemetry packets
    const multiMotorBaseName = plotKeyToMultiMotorInfo( plotKey )?.baseName;
    if ( multiMotorBaseName && multiMotorBaseName in multiMotorTelemDisplayUnits ) {
        return multiMotorTelemDisplayUnits[ multiMotorBaseName as keyof IMultiMotorTelemetryMessage ];
    }
    // If header is present from SLNT file, use that instead of TelemToml
    const units = _playbackSlntHeader?.fieldsMetadata[ plotKey ]?.units;
    if ( units ) return units;

    const telemDefs = (telem as TelemToml).telemetry.definitions;
    for ( let i = 0; i < telemDefs.length; i++ ) {
        const telemDef = telemDefs[i];
        if ( plotKey === telemDef.name ) {
            return telemDef.units ?? "";
        }
    }
    return "";
}

const threadPool: Thread[] = [];
for ( let i = 0; i < NUM_THREADS; i++ ) {
    //Instantiate worker here
    const worker = new Worker(new URL('./packet.worker', import.meta.url), {
        type: 'module'
    });

    const thread = {
        api: wrap<IPacketWorkerApi>( worker ),
        numTasks: 0
    };
    threadPool.push( thread );
    // Call this on startup to workaround a rollup issue.
    thread.api.loadTelemToml( telem as TelemToml );
}

let threadIdx = 0;

const withThreadApi = async <T>( thread: Thread, fn: ( api: Remote<IPacketWorkerApi> ) => Promise<T> ): Promise<T> => {
    threadIdx = ( threadIdx + 1 ) % threadPool.length;
    thread.numTasks++;
    const val = await fn( thread.api );
    thread.numTasks--;
    return val;
};

let servoPulseTransformFn: ServoPulseTransformFn;

const servoPulseTransform  = ( packet: RealTimeDataPacket ) => {
    if ( servoPulseTransformFn !== undefined ) {
        const p = packet.packet as IRealtimeDataMessage;
        p.servoPulse = servoPulseTransformFn( p.servoPulse );
    }
};

let _telemSet: { fieldName: string, fieldType: TelemIdxType }[] = [];
let _playbackSlntHeader: SlntHeader | undefined;

export const packetThreadPoolApi = {
    handlePackets: async ( packets: RealTimeDataPacket[] ) => {
        for ( const packet of packets ) {
            servoPulseTransform( packet ); // Can modify packet
            updateTimeWindow( packet.timestampMs );
        }

        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.handlePackets( packets ) ) );
        }
        await Promise.all( promises );
    },
    loadTelemHeader: async ( header: SlntHeader ) => {
        _playbackSlntHeader = header;
        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.loadTelemHeader( header ) ) );
        }
        await Promise.all( promises );
    },
    loadTelemMap: async ( telemSet: { fieldName: string, fieldType: TelemIdxType }[] ) => {
        _telemSet = telemSet;
        _notifyTelemSet();
        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.loadTelemMap( telemSet ) ) );
        }
        await Promise.all( promises );
    },
    loadMultiMotorTelemSet: async ( multiMotorTelemSet: { fieldName: string, fieldType: TelemIdxType }[] ) => {
        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.loadMultiMotorTelemSet( multiMotorTelemSet ) ) );
        }
        await Promise.all( promises );
    },
    getTelemSet: (): { fieldName: string, fieldType: TelemIdxType }[] => {
        return _telemSet;
    },
    handleVariableTelemetry: async ( timestampMs: number, telemId: number, telemBytestream: Uint8Array ) => {
        updateTimeWindow( timestampMs );
        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.handleVariableTelemetry( timestampMs, telemId, telemBytestream ) ) );
        }
        await Promise.all( promises );
    },
    handleVarTelemPlayback: async ( vtss: VariableTelemSamples[] ) => {
        vtss.forEach( vts => updateTimeWindow( vts.ts ) );
        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.handleVarTelemPlayback( vtss ) ) );
        }
        await Promise.all( promises );
    },
    handleMultiMotorTelemPlayback: async ( mmtss: MultiMotorTelemSamples[] ) => {
        mmtss.forEach( mmts => updateTimeWindow( mmts.ts ) );
        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.handleMultiMotorTelemPlayback( mmtss ) ) );
        }
        await Promise.all( promises );
    },
    handleMultiMotorTelemetry: async (
        timestampMs: number,
        packet: IMultiMotorTelemetryMessage,
    ) => {
        updateTimeWindow(timestampMs);
        const promises = [];
        for (let i = 0; i < threadPool.length; i++) {
            promises.push( withThreadApi(threadPool[i], api => api.handleMultiMotorTelemetry( timestampMs, packet ) ) );
        }
        await Promise.all(promises);
    },
    setActuatorDispositions: async ( subActuators: SubActuators ) => {
        _subActuators = subActuators;
        _notifySubActuators();
        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.setActuatorDispositions( subActuators ) ) );
        }
        await Promise.all( promises );
    },
    // Caller must ensure plotKeys only contains valid numeric telemetry field names. See getNumericValue calls in packet.worker.ts.
    renderBitmap: async ( plotKeys: PlotKey[], timeWindow: TimeWindow, width: number, height: number, xHoverValue: number | undefined ) => {
        return await withThreadApi( threadPool[ threadIdx ], api => api.renderBitmap( plotKeys, timeWindow, width, height, xHoverValue ) );
    },
    getLatestTelemValueNotAfter: async ( plotKey: PlotKey, timestampMs: number ) => {
        const fn = (api: Remote<IPacketWorkerApi>): Promise<TelemValue | null> => {
            return api.getLatestTelemValueNotAfter( plotKey, timestampMs );
        };
        return await withThreadApi( threadPool[ threadIdx ], fn );
    },
    export: async ( filename: string, exportType: ExportType, timeWindow: TimeWindow, downloadFn: ( filename: string, file: string ) => void ) => {
        return await withThreadApi( threadPool[ threadIdx ], api => api.export( filename, exportType, timeWindow, downloadFn ) );
    },
    getMotorPosition: async () => {
        return await withThreadApi( threadPool[ threadIdx ], api => api.getMotorPosition() );
    },
    getPositionSensorReading: async () => {
        return await withThreadApi( threadPool[ threadIdx ], api => api.getPositionSensorReading() );
    },
    setServoPulseTransformFn: async ( fn: ServoPulseTransformFn ) => {
        servoPulseTransformFn = fn;
    },
    clear: async () => {
        _telemSet = [];
        _subActuators = {};
        _notifyTelemSet();
        _notifySubActuators();
        const promises = [];
        for ( let i = 0; i < threadPool.length; i++ ) {
            promises.push( withThreadApi( threadPool[ i ], api => api.clear() ) );
        }
        await Promise.all( promises );
        realtimeMetadata = defaultRealtimeMetadata();
        servoPulseTransformFn = undefined;
    },
};
